import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiService } from "../services/apiService";
import { API_ENDPOINTS } from "../config/api";
import { handleError } from "../utils/errorHandler";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { hrHomePath } from "../utils/hrAccess";
import "./login.css";

const FEATURES = [
  "Leads, clients & opportunities",
  "Pipeline & site-visit tracking",
  "Reports built for your team",
];

const Login = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await apiService.post(API_ENDPOINTS.auth.login, {
        email: formData.email.trim(),
        password: formData.password,
      });
      localStorage.setItem("authToken", res.token);
      localStorage.setItem("userRole", res.user.role);
      localStorage.setItem("userId", res.user.id);
      localStorage.setItem("userEmail", res.user.email);

      login({ role: res.user.role, token: res.token });

      navigate(hrHomePath(res.user.role));
      toast.success("Login successful");
    } catch (err) {
      let errorMessage = "An error occurred during login";

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);

      handleError(err, {
        context: "Login - handleSubmit",
        showToast: false,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true">
        <div className="login-bg__mesh" />
        <div className="login-bg__grid" />
        <span className="login-orb login-orb--1" />
        <span className="login-orb login-orb--2" />
        <span className="login-orb login-orb--3" />
        <span className="login-orb login-orb--4" />
      </div>

      <div className="login-shell">
        <aside className="login-showcase">
          <div className="login-showcase__badge login-anim" style={{ animationDelay: "0.1s" }}>
            <span className="login-showcase__pulse" />
            CRM Platform
          </div>

          <div className="login-showcase__brand login-anim" style={{ animationDelay: "0.2s" }}>
            <img
              src="/kwike1.png"
              alt=""
              className="login-showcase__logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h2 className="login-showcase__title">Rewarealtors</h2>
          </div>

          <p className="login-showcase__tagline login-anim" style={{ animationDelay: "0.35s" }}>
            Manage your pipeline with{" "}
            <span className="login-showcase__gradient-text">clarity</span> and control.
          </p>

          <ul className="login-showcase__features">
            {FEATURES.map((text, i) => (
              <li
                key={text}
                className="login-showcase__feature login-anim"
                style={{ animationDelay: `${0.45 + i * 0.12}s` }}
              >
                <span className="login-showcase__check" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {text}
              </li>
            ))}
          </ul>

          <div className="login-showcase__stats login-anim" style={{ animationDelay: "0.85s" }}>
            <div className="login-stat">
              <span className="login-stat__value">24/7</span>
              <span className="login-stat__label">Access</span>
            </div>
            <div className="login-stat">
              <span className="login-stat__value">Secure</span>
              <span className="login-stat__label">Sign-in</span>
            </div>
          </div>
        </aside>

        <main className="login-panel">
          <div className="login-card login-anim" style={{ animationDelay: "0.25s" }}>
            <div className="login-card__shine" aria-hidden="true" />
            <div className="login-card__border" aria-hidden="true" />

            <header className="login-card__header">
              <p className="login-card__eyebrow">Welcome back</p>
              <h1 className="login-card__title">Sign in</h1>
              <p className="login-card__subtitle">
                Enter your credentials to open the dashboard.
              </p>
            </header>

            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <div className="login-field login-anim" style={{ animationDelay: "0.4s" }}>
                <label htmlFor="login-email">Email address</label>
                <div className="login-field__wrap">
                  <span className="login-field__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 6h16v12H4z" />
                      <path d="M4 8l8 5 8-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="login-field login-anim" style={{ animationDelay: "0.5s" }}>
                <label htmlFor="login-password">Password</label>
                <div className="login-field__wrap">
                  <span className="login-field__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M8 11V8a4 4 0 118 0v3" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="login-field__toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {error && (
                <div className="login-error login-error--shake" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="login-submit login-anim"
                style={{ animationDelay: "0.6s" }}
                disabled={loading}
              >
                <span className="login-submit__bg" aria-hidden="true" />
                <span className="login-submit__content">
                  {loading ? (
                    <>
                      <span className="login-submit__spinner" aria-hidden="true" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign in
                      <svg
                        className="login-submit__arrow"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  )}
                </span>
              </button>
            </form>

            <p className="login-card__footer login-anim" style={{ animationDelay: "0.7s" }}>
              Authorized team members only
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
